/**
 * /checkin/home — mobile home screen post-onboarding.
 *
 * Phase 2 shows greeting + Talk-Credit balance stub + Start-check-in CTA
 * (routes to a friendly placeholder — real conversation lands in Phase 3).
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Sparkles, User, LogOut } from "lucide-react";
import { CheckInShell, CheckInLogo, PrimaryButton, GhostButton, CHECKIN_PALETTE } from "./_shell";
import { checkinApi, getToken, clearAuth } from "@/lib/checkinApi";

export default function CheckInHome() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const [verifyState, setVerifyState] = useState(null);

  useEffect(() => {
    if (!getToken()) {
      navigate("/checkin", { replace: true });
      return;
    }
    (async () => {
      try {
        const d = await checkinApi.home();
        if (!d.onboarding_complete) {
          navigate("/checkin/onboarding", { replace: true });
          return;
        }
        setData(d);
        // Phase 1.5 — surface verification status (non-blocking).
        checkinApi.verifyStatus()
          .then((s) => setVerifyState(s))
          .catch(() => {});
      } catch (e) {
        if (e.status === 401) {
          clearAuth();
          navigate("/checkin/signin", { replace: true });
        } else {
          setErr(e.message || "Couldn't load your space.");
        }
      }
    })();
  }, [navigate]);

  function signOut() {
    clearAuth();
    navigate("/checkin", { replace: true });
  }

  if (!data && !err) {
    return (
      <CheckInShell testid="checkin-home-loading">
        <div className="mt-24 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: CHECKIN_PALETTE.purple }} />
        </div>
      </CheckInShell>
    );
  }

  if (err) {
    return (
      <CheckInShell testid="checkin-home-error">
        <div className="mt-24 text-center">
          <p className="text-sm" style={{ color: CHECKIN_PALETTE.danger }}>{err}</p>
          <div className="mt-6">
            <GhostButton onClick={() => window.location.reload()} testid="home-retry">Try again</GhostButton>
          </div>
        </div>
      </CheckInShell>
    );
  }

  const credits = data.credits_balance || 0;

  return (
    <CheckInShell testid="checkin-home">
      <div className="pt-2 flex items-center justify-between">
        <CheckInLogo small />
        <button
          onClick={signOut}
          data-testid="home-signout"
          className="flex items-center gap-1.5 text-[11px]"
          style={{ color: "#6C6478" }}
        >
          <LogOut className="w-3.5 h-3.5" /> Sign out
        </button>
      </div>

      <div className="mt-8">
        <div className="text-[10px] font-semibold uppercase tracking-[0.28em]"
             style={{ color: CHECKIN_PALETTE.purple }}>
          Your check-in
        </div>
        <h1
          className="mt-2 font-serif text-3xl leading-tight"
          style={{ color: CHECKIN_PALETTE.ink, fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          data-testid="home-greeting"
        >
          {data.greeting}
        </h1>
      </div>

      {verifyState && !verifyState.profile?.verified_phone
       && verifyState.conversation_gate?.state !== "not_required" && (
        <button
          onClick={() => navigate("/checkin/verify?from=/checkin/home")}
          className="mt-4 w-full rounded-2xl p-3 text-left text-xs"
          style={{
            background: verifyState.conversation_gate?.state === "grace_exhausted"
              ? "#FEEEF1" : "#FDF6E4",
            border: `1px solid ${verifyState.conversation_gate?.state === "grace_exhausted"
              ? "#B4223C33" : "#F1E3B0"}`,
            color: verifyState.conversation_gate?.state === "grace_exhausted"
              ? "#B4223C" : "#8B6B14",
          }}
          data-testid="home-verify-banner"
        >
          <div className="font-medium">
            {verifyState.conversation_gate?.state === "grace_exhausted"
              ? "Phone verification required to start a new conversation."
              : "Help us secure your Check In — verify your phone."}
          </div>
          <div className="mt-1 opacity-80">
            {verifyState.conversation_gate?.state === "grace_exhausted"
              ? "Tap to verify now. Your history stays intact."
              : `Tap to verify. You have ${Math.max(0,
                  (verifyState.conversation_gate?.grace_max || 0) -
                  (verifyState.conversation_gate?.logins || 0))} sign-in${((verifyState.conversation_gate?.grace_max || 0) - (verifyState.conversation_gate?.logins || 0)) === 1 ? "" : "s"} of grace remaining.`}
          </div>
        </button>
      )}

      <div
        className="mt-6 rounded-3xl p-5"
        style={{
          background: `linear-gradient(135deg, ${CHECKIN_PALETTE.purple} 0%, #3A1362 100%)`,
          color: "white",
          boxShadow: "0 20px 40px -22px rgba(92,30,138,0.7)",
        }}
        data-testid="home-credit-card"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] font-semibold opacity-80">
              Talk Credits
            </div>
            <div className="mt-1 font-serif text-4xl"
                 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                 data-testid="home-credit-balance">
              {credits}
            </div>
            <div className="text-[11px] opacity-80 mt-0.5"
                 data-testid="home-credit-exchanges">
              {credits === 0
                ? "Add credits to begin a substantive check-in."
                : `${credits} Talk Credit${credits === 1 ? "" : "s"} — up to ${credits * 5} exchanges`}
            </div>
          </div>
          <Sparkles className="w-8 h-8 opacity-70" />
        </div>
      </div>

      <div className="mt-6 space-y-3">
        <PrimaryButton
          onClick={() => navigate("/checkin/conversation")}
          testid="home-start-checkin"
        >
          Start today&rsquo;s check-in
        </PrimaryButton>
        <GhostButton
          onClick={() => navigate("/checkin/plans")}
          testid="home-add-credits"
        >
          Add Talk Credits
        </GhostButton>
        <button
          onClick={() => navigate("/checkin/history")}
          className="w-full text-[12px] underline mt-1"
          style={{ color: CHECKIN_PALETTE.purple }}
          data-testid="home-my-checkins-link"
        >
          My Check-Ins
        </button>
        <button
          onClick={() => navigate("/checkin/journey")}
          className="w-full text-[12px] underline"
          style={{ color: CHECKIN_PALETTE.purple }}
          data-testid="home-journey-link"
        >
          Your Journey Timeline
        </button>
      </div>

      <div className="mt-auto pt-8 flex items-center justify-center gap-4 text-[11px]"
           style={{ color: "#6C6478" }} data-testid="home-footer">
        <Link to="/checkin/policies" data-testid="home-policies-link" className="hover:underline">
          Policies
        </Link>
        <span>·</span>
        <span className="inline-flex items-center gap-1">
          <User className="w-3 h-3" /> {data.first_name}
        </span>
        <span>·</span>
        <span>Technology powered by KCIS</span>
      </div>
    </CheckInShell>
  );
}
